import { IAMService, User } from '@vendasta/iamv2';
import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, map, Observable, of, ReplaySubject, startWith } from 'rxjs';
import { CrmDependencies, CrmInjectionToken, SimplifiedUser, UserWithDisplayName } from '../tokens-and-interfaces';
import { convertToReadableUsers, getMultiUsers } from '../utils';
import { catchError, debounceTime, distinctUntilChanged, shareReplay, switchMap, tap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable()
export class ActivityAssigneeService {
  private readonly allUsers$$: ReplaySubject<UserWithDisplayName[]> = new ReplaySubject(1);
  public readonly searchTerm$$ = new ReplaySubject<string>(1);
  private readonly searchTerm$ = this.searchTerm$$.asObservable();
  public readonly allUsers$ = this.allUsers$$.asObservable();

  private readonly fetchedUsers$$ = new BehaviorSubject<UserWithDisplayName[]>([]);
  public readonly fetchedUsers$ = this.fetchedUsers$$.asObservable();

  constructor(
    @Inject(CrmInjectionToken) private readonly config: CrmDependencies,
    @Inject(IAMService) private readonly iamService: IAMService,
  ) {
    this.searchTerm$
      .pipe(
        startWith(''),
        takeUntilDestroyed(),
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((searchTerm) => this.config.services?.userService?.searchUsers?.(searchTerm) ?? of([])),
        map((users: SimplifiedUser[]) => convertToReadableUsers(users)),
        catchError((error) => {
          console.error(error);
          return of([]);
        }),
      )
      .subscribe((users) => {
        this.allUsers$$.next(users);
      });
  }

  getMultiUsers(userIds: string[]): Observable<UserWithDisplayName[]> {
    const cachedUser$ = this.allUsers$.pipe(
      map((users) => {
        const fetched = this.fetchedUsers$$.value;
        const userHashMap = new Map<string, UserWithDisplayName | undefined>();
        userIds.forEach((userId) =>
          userHashMap.set(
            userId,
            users.find((u) => u.userId === userId) ?? fetched.find((u) => u.userId === userId),
          ),
        );
        return userHashMap;
      }),
      shareReplay(1),
    );

    const fetchedMissingUsers$ = cachedUser$.pipe(
      map((cachedUsers) => userIds.filter((userId) => cachedUsers.get(userId) === undefined)),
      switchMap((idsToGet) => {
        if (idsToGet.length === 0) {
          return of([] as User[]);
        }
        return getMultiUsers(idsToGet, this.config, this.iamService);
      }),
      catchError((error) => {
        console.error(error);
        return of([] as User[]);
      }),
      map((users) => convertToReadableUsers(users)),
      tap((users) => {
        if (users.length === 0) return;
        const current = this.fetchedUsers$$.value;
        const newUsers = users.filter((u) => u.userId && !current.some((c) => c.userId === u.userId));
        if (newUsers.length > 0) {
          this.fetchedUsers$$.next([...current, ...newUsers]);
        }
      }),
    );

    return combineLatest([cachedUser$, fetchedMissingUsers$]).pipe(
      map(([cachedUsers, fetchedUsers]) => {
        fetchedUsers.forEach((user) => cachedUsers.set(user.userId, user));
        return [...cachedUsers.values()].filter(Boolean) as UserWithDisplayName[];
      }),
    );
  }
}
